import { numberDecimal } from "../../../utils/numbers";
import { getFromStatisticsAdsCampaign } from "../../../services/adsCampaigns";
import {
  METRICS_NOT_FOUND_MESSAGE,
  OVERVIEW_HOME_STATISTICS,
  TOTAL_CLICKS,
  TOTAL_IMPRESSIONS,
  TOTAL_ROAS_GOOGLE,
  TOTAL_ROAS_META,
  TOTAL_ROAS_TIK_TOK,
  TOTAL_RESULTS,
  TOTAL_SPEND,
} from "../../../i18n/keysTranslations";
import { GOOGLE, META, TIKTOK } from "../../../utils/constants";

const STATISTICS_NON_PLATFORM_KEYS = new Set(["startDateTime", "endDateTime"]);

const getStatisticsPlatformKeys = (statistics = {}) =>
  Object.keys(statistics).filter(
    (key) => !STATISTICS_NON_PLATFORM_KEYS.has(key)
  );

const getSingleDateLabel = (value, locale) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString(locale);
};

const getSummaryText = ({ campaign }) => {
  return (
    campaign?.generatedSummaryText ||
    campaign?.summaryText ||
    campaign?.summary ||
    "-"
  );
};

const getLearnings = ({ campaign }) => {
  if (Array.isArray(campaign?.learnings)) return campaign.learnings;
  if (Array.isArray(campaign?.generatedLearnings))
    return campaign.generatedLearnings;
  return [];
};

const getNormalizedLearnings = ({ learnings }) => {
  if (!Array.isArray(learnings)) return [];
  return learnings.map((item) => ({
    platform: item?.platform || "-",
    topLearnings: Array.isArray(item?.topLearnings) ? item.topLearnings : [],
    worked: item?.worked || { tags: [] },
    notWorked: item?.notWorked || { tags: [] },
  }));
};

const getGrowthPercentage = (current, previous) => {
  if (
    (previous === 0 || previous === undefined || previous === null) &&
    !current
  ) {
    return 0;
  }
  if (
    (previous === 0 || previous === undefined || previous === null) &&
    current > 0
  ) {
    return 100;
  }
  if (previous === undefined || previous === null) return null;
  if (current === undefined || current === null) return null;
  return numberDecimal(((current - previous) / Math.abs(previous)) * 100, 1);
};

const getOverviewCards = ({
  statistics = {},
  previousPeriodStatistics = {},
  platforms = [],
  t = (value) => value,
}) => {
  const getTotals = (stats) => {
    let impressions = 0;
    let clicks = 0;
    let results = 0;
    let spend = 0;
    getStatisticsPlatformKeys(stats).forEach((platform) => {
      impressions += stats?.[platform]?.campaign?.impressions || 0;
      clicks += stats?.[platform]?.campaign?.click || 0;
      results += stats?.[platform]?.campaign?.mainAction || 0;
      spend += stats?.[platform]?.campaign?.spend || 0;
    });
    return { impressions, clicks, results, spend };
  };

  const current = getTotals(statistics);
  const previous = getTotals(previousPeriodStatistics);
  const firstPlatform = platforms[0];
  const currency = firstPlatform
    ? statistics?.[firstPlatform]?.campaign?.currency || ""
    : "";

  const baseCards = [
    {
      key: "impressions",
      label: t(TOTAL_IMPRESSIONS),
      value: current.impressions,
      previousValue: previous.impressions,
      growth: getGrowthPercentage(current.impressions, previous.impressions),
    },
    {
      key: "clicks",
      label: t(TOTAL_CLICKS),
      value: current.clicks,
      previousValue: previous.clicks,
      growth: getGrowthPercentage(current.clicks, previous.clicks),
    },
    {
      key: "results",
      label: t(TOTAL_RESULTS),
      value: current.results,
      previousValue: previous.results,
      growth: getGrowthPercentage(current.results, previous.results),
    },
    {
      key: "spend",
      label: t(TOTAL_SPEND),
      value: numberDecimal(current.spend, 2),
      previousValue: numberDecimal(previous.spend, 2),
      growth: getGrowthPercentage(current.spend, previous.spend),
      currency,
    },
  ];

  const getRoasLabel = (platform) => {
    if (platform === GOOGLE) return t(TOTAL_ROAS_GOOGLE);
    if (platform === TIKTOK) return t(TOTAL_ROAS_TIK_TOK);
    if (platform === META) return t(TOTAL_ROAS_META);
    return `${String(platform || "").toUpperCase()} ROAS`;
  };

  const roasCards = platforms.reduce((acc, platform) => {
    const currentRoas = statistics?.[platform]?.campaign?.roas;
    const previousRoas = previousPeriodStatistics?.[platform]?.campaign?.roas;

    if (
      currentRoas === undefined ||
      currentRoas === null ||
      currentRoas === 0
    ) {
      return acc;
    }

    acc.push({
      key: `roas-${platform}`,
      label: getRoasLabel(platform),
      value: numberDecimal(currentRoas),
      previousValue: numberDecimal(previousRoas || 0),
      growth: getGrowthPercentage(currentRoas, previousRoas),
      platform,
    });
    return acc;
  }, []);

  return [...baseCards, ...roasCards];
};

const getOverviewSnapshotSections = ({ overviewChartSnapshots = [] }) => {
  const [trends, cpm, cpc, cpa] = overviewChartSnapshots || [];
  return {
    trends: {
      title: "Performance Trends",
      image: trends || null,
    },
    comparison: [
      { key: "cpm", title: "CPM", image: cpm || null },
      { key: "cpc", title: "CPC", image: cpc || null },
      { key: "cpa", title: "CPA", image: cpa || null },
    ],
  };
};

const getInsights = ({ filteredLogs = [], locale }) => {
  return filteredLogs.map((log) => ({
    date: new Date(log.creationTime).toLocaleString(locale),
    source: log.platform || "-",
    text:
      log?.changes?.[0]?.newValue?.title ||
      log?.changes?.[0]?.newValue?.description ||
      "-",
  }));
};

export const formatReportData = ({
  campaign,
  reportConfiguration,
  breakdown,
  breakdownLabel,
  filteredLogs,
  logoUrl,
  businessName,
  reportGeneratedByLink,
  footerDateText,
  footerGeneratedByText,
  showFooterLink = true,
  summaryText,
  learnings,
  overviewChartSnapshots = [],
  metricsTableSnapshots = [],
  performingAssetsSnapshots = [],
  overviewSectionTitle = "Overview",
  locale,
  t = (value) => value,
}) => {
  const { statistics, previousPeriodStatistics, platforms } =
    getFromStatisticsAdsCampaign(campaign);
  const generatedAtLabel = new Date().toLocaleString(locale);

  return {
    campaignName:
      campaign?.platformsData?.[campaign?.platforms?.[0]]?.name ||
      campaign?.name ||
      "-",
    locale,
    businessName: businessName || "",
    documentMeta: {
      title: campaign?.name || t(OVERVIEW_HOME_STATISTICS),
      subject: t(OVERVIEW_HOME_STATISTICS),
    },
    logoUrl,
    footerLink: reportGeneratedByLink,
    showFooterLink,
    footerDateText: footerDateText || `Generated: ${generatedAtLabel}`,
    footerGeneratedByText: footerGeneratedByText || "",
    summaryText: summaryText || getSummaryText({ campaign }),
    configuration: {
      startDateLabel: getSingleDateLabel(
        reportConfiguration?.startDateTime,
        locale
      ),
      endDateLabel: getSingleDateLabel(
        reportConfiguration?.endDateTime,
        locale
      ),
      breakdown:
        breakdownLabel || breakdown || reportConfiguration?.breakdown || "-",
    },
    learnings: getNormalizedLearnings({
      learnings:
        Array.isArray(learnings) && learnings.length > 0
          ? learnings
          : getLearnings({ campaign }),
    }),
    overview: {
      sectionTitle: overviewSectionTitle,
      cards: getOverviewCards({
        statistics,
        previousPeriodStatistics,
        platforms,
        t,
      }),
      snapshots: getOverviewSnapshotSections({ overviewChartSnapshots }),
    },
    insights: getInsights({ filteredLogs, locale }),
    metricsTableSnapshots,
    performingAssetsSnapshots,
    fallbackTexts: {
      noTableSnapshot: t(METRICS_NOT_FOUND_MESSAGE),
    },
  };
};
